import { formatQuantity } from '@/lib/format'
import type { FeatureLimit, FeatureMap } from '@/schemas/subscriptions'

/**
 * What each feature flag is called on a screen.
 *
 * mm-be publishes the 47 keys and no words for any of them, so the web owns
 * this map. Two rules hold everywhere it is used:
 *
 *  - A key with no entry here is never drawn. A flag mm-be adds tomorrow does
 *    not appear as `planning.tax_helper` on a plan card.
 *  - A card claims only what the plan's flags grant (owner decision,
 *    2026-10-01) — so on dev's catalogue some cards are thin. That is the data.
 *
 * `label` is the checklist line, in Figma's voice ("Price alerts that reach
 * you"). `short` is the lowercase noun a one-line summary is built from
 * ("Alerts, screener and compare.").
 */

export type FeatureGroup =
  'market' | 'company' | 'tools' | 'book' | 'alerts' | 'brokers' | 'planning' | 'learning'

/** Comparison-table sections, in the order a table draws them. */
export const FEATURE_GROUPS: readonly { id: FeatureGroup; label: string }[] = [
  { id: 'market', label: 'Market' },
  { id: 'company', label: 'Company data' },
  { id: 'tools', label: 'Tools' },
  { id: 'book', label: 'Your book' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'brokers', label: 'Brokers' },
  { id: 'planning', label: 'Planning' },
  { id: 'learning', label: 'Learning' },
]

export interface FeatureCopy {
  label: string
  short: string
  group: FeatureGroup
  /** Position within its group. */
  order: number
}

type CopyRow = readonly [key: string, label: string, short: string, group: FeatureGroup]

/** Listed in each group's display order. */
const COPY_ROWS: readonly CopyRow[] = [
  ['market.live', 'Live tape and the NEPSE index', 'live tape', 'market'],
  ['market.live_chart', 'Live price charts', 'live charts', 'market'],
  ['market.technical_chart', 'Technical charts with indicators', 'technical charts', 'market'],
  ['market.depth', 'Market depth, level by level', 'depth', 'market'],
  ['market.floorsheet', 'Floorsheet history, trade by trade', 'floorsheet', 'market'],
  ['market.hot_stocks', 'Hot stocks of the day', 'hot stocks', 'market'],
  ['market.stock_news', 'News on the stocks you follow', 'stock news', 'market'],
  ['mutual_funds', 'Mutual funds, compared', 'mutual funds', 'market'],

  ['company.price_history', 'Price history for every company', 'price history', 'company'],
  ['company.dividend', 'Dividend history', 'dividends', 'company'],
  ['company.week_52_change', '52-week high and low', '52-week range', 'company'],
  ['company.swot', 'Strengths and risks at a glance', 'SWOT', 'company'],
  ['company.balance_sheet', 'Balance sheets', 'balance sheets', 'company'],
  ['company.income_statement', 'Income statements', 'income statements', 'company'],
  ['company.cash_flow', 'Cash flow statements', 'cash flow', 'company'],
  ['company.quarterly_report', 'Quarterly reports', 'quarterly reports', 'company'],
  ['company.report', 'Annual reports', 'reports', 'company'],
  ['company.ipo_information', 'IPO details for every listing', 'IPO details', 'company'],

  ['screener', 'Screener to sift the market', 'screener', 'tools'],
  ['company.compare', 'Compare companies side by side', 'compare', 'tools'],
  ['market.stock_basket', 'Baskets of stocks to follow', 'baskets', 'tools'],
  ['signals', 'AI signals on stocks', 'AI signals', 'tools'],
  ['ipo.bulk_result', 'IPO results for many accounts at once', 'IPO results', 'tools'],

  ['portfolio', 'Your portfolio in one place', 'portfolio', 'book'],
  ['watchlist', 'Watchlists for what you follow', 'watchlists', 'book'],
  ['portfolio.import', 'Broker statement import', 'statement import', 'book'],
  ['portfolio.transactions', 'Every buy and sell, recorded', 'transactions', 'book'],
  ['portfolio.shareholders', 'A book for each family member', 'family books', 'book'],
  ['portfolio.pnl_visualisation', 'Profit and loss, charted', 'profit and loss', 'book'],

  ['alerts', 'Price alerts that reach you', 'alerts', 'alerts'],
  ['notify.push', 'Alerts on your phone', 'push alerts', 'alerts'],
  ['notify.email', 'Alerts by email', 'email alerts', 'alerts'],
  ['notify.sms', 'Alerts by SMS', 'SMS alerts', 'alerts'],

  ['broker.analysis', 'Broker analysis', 'broker analysis', 'brokers'],
  ['broker.profile', 'Broker profiles', 'broker profiles', 'brokers'],
  ['broker.list', 'Every broker in one list', 'broker list', 'brokers'],
  ['broker.review', 'Broker reviews', 'broker reviews', 'brokers'],

  ['planning.valuation_calculator', 'Valuators for a fair price', 'valuators', 'planning'],
  ['planning.calculators', 'Money calculators', 'calculators', 'planning'],
  ['planning.goal_planner', 'A planner for your goals', 'goal planner', 'planning'],
  ['planning.financial_planner', 'A plan for your money', 'financial planner', 'planning'],
  ['planning.monthly_budget', 'A monthly budget', 'budget', 'planning'],
  ['planning.risk_appetite', 'Know how much risk suits you', 'risk check', 'planning'],
  ['planning.spending_habit', 'See where your money goes', 'spending check', 'planning'],
  ['planning.report_card', 'Your money report card', 'report card', 'planning'],

  ['newsletter', 'The weekly letter', 'newsletters', 'learning'],
  ['articles', 'Articles that explain the market', 'articles', 'learning'],
  ['articles.elite', 'Members-only articles', 'member articles', 'learning'],
]

/** Every key the web can name, read through `featureCopy`. */
export const FEATURE_COPY: ReadonlyMap<string, FeatureCopy> = new Map(
  COPY_ROWS.map(([key, label, short, group]) => [
    key,
    {
      label,
      short,
      group,
      order: COPY_ROWS.filter((row) => row[3] === group).findIndex((row) => row[0] === key),
    },
  ]),
)

export const featureCopy = (key: string): FeatureCopy | null => FEATURE_COPY.get(key) ?? null

/**
 * The order a plan's highlights are picked in — Figma's own bullets first
 * (Sadasya's alerts, screener, compare, baskets, import; then Guru's depth,
 * floorsheet, valuators, letter; then Free's tape, watchlist, book), every
 * other named key after them in `FEATURE_COPY` order.
 */
const HIGHLIGHT_PRIORITY: readonly string[] = [
  'alerts',
  'screener',
  'company.compare',
  'market.stock_basket',
  'portfolio.import',
  'market.depth',
  'market.floorsheet',
  'planning.valuation_calculator',
  'newsletter',
  'signals',
  'market.live',
  'watchlist',
  'portfolio',
]

/** Curated keys first, then every other named key in `FEATURE_COPY` order. */
const HIGHLIGHT_RANK: ReadonlyMap<string, number> = new Map(
  [...new Set([...HIGHLIGHT_PRIORITY, ...FEATURE_COPY.keys()])].map((key, rank) => [key, rank]),
)

const highlightRank = (key: string): number => HIGHLIGHT_RANK.get(key) ?? HIGHLIGHT_RANK.size

/**
 * A flag's tri-state as words, or null when the plan does not include it.
 *
 *   enabled false (or the key absent)   null — not included
 *   enabled, limit null                 "Included" — no ceiling STATED, which
 *                                       is not a promise of "unlimited"
 *   enabled, limit 0                    "Included" — contradictory on the wire
 *                                       (dev's plan 11 `alerts`); 0 is never
 *                                       printed as a cap on something granted
 *   enabled, limit N > 0                "Up to N"
 */
export function formatFeatureLimit(flag: FeatureLimit | undefined): string | null {
  if (flag?.enabled !== true) return null
  if (flag.limit === null || flag.limit <= 0) return 'Included'
  return `Up to ${formatQuantity(flag.limit)}`
}

export interface FeatureHighlight {
  key: string
  label: string
  short: string
  /** `formatFeatureLimit`'s words — "Included" or "Up to 100". */
  limitText: string
}

export interface HighlightOptions {
  /**
   * Leave out what this map already grants — Guru's card says what it adds
   * over Sadasya ("…on top"), not the same three lines again.
   */
  except?: FeatureMap | undefined
}

/**
 * Up to `count` of the plan's granted features that the web can name, in
 * Figma's bullet order. `Infinity` lists them all.
 */
export function highlightFeatures(
  features: FeatureMap,
  count: number,
  options: HighlightOptions = {},
): FeatureHighlight[] {
  return Object.entries(features)
    .flatMap(([key, flag]): FeatureHighlight[] => {
      const copy = featureCopy(key)
      const limitText = formatFeatureLimit(flag)
      if (copy === null || limitText === null) return []
      if (options.except?.[key]?.enabled === true) return []
      return [{ key, label: copy.label, short: copy.short, limitText }]
    })
    .sort((a, b) => highlightRank(a.key) - highlightRank(b.key))
    .slice(0, count)
}

/**
 * The card's one-line tagline from its top three highlights: "Alerts,
 * screener and compare." Null when the plan grants nothing the web can name —
 * the card then draws no tagline rather than an invented one.
 */
export function planSummary(features: FeatureMap, options: HighlightOptions = {}): string | null {
  const [first, ...rest] = highlightFeatures(features, 3, options).map((item) => item.short)
  if (first === undefined) return null
  const last = rest.pop()
  const sentence = last === undefined ? first : `${[first, ...rest].join(', ')} and ${last}`
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`
}
